import React, { createContext, useState, useEffect, useMemo } from "react";

// Create CartContext
export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const storedCart = localStorage.getItem("cart");
    return storedCart ? JSON.parse(storedCart) : [];
  });

  // Save cart to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // Total quantity in cart
  const totalQuantity = useMemo(() => 
    cart.reduce((sum, item) => sum + item.quantity, 0), [cart]
  );

  // Subtotal (without shipping)
  const subtotal = useMemo(() =>
    cart.reduce((sum, item) => sum + item.discount_price * item.quantity, 0), [cart]
  );

  // Shipping based on quantity
  const shipping = useMemo(() => (totalQuantity === 1 ? 100 : 0), [totalQuantity]);

  // Final total with shipping
  const total = useMemo(() => subtotal + shipping, [subtotal, shipping]);

  // Add to Cart
  const addToCart = (item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((i) => i.id === item.id);
      if (existingItem) {
        return prevCart.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prevCart,
        {
          ...item,
          image: `${item.image}`,
          quantity: 1,
        },
      ];
    });
  };

  // Decrement Quantity
  const decrementQuantity = (id) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((i) => i.id === id);
      if (existingItem && existingItem.quantity > 1) {
        return prevCart.map((i) =>
          i.id === id ? { ...i, quantity: i.quantity - 1 } : i
        );
      } else {
         return prevCart.filter((item) => item.id !== id);
      }
    });
  };

  // Remove from Cart
  const removeFromCart = (id) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== id));
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        addToCart,
        removeFromCart,
        decrementQuantity,
        subtotal,
        shipping,
        total,
        totalQuantity,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
